import React, { useCallback, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { useCreateWithAiItem } from '@jira/ai-first-create__app/src/useCreateWithAiItem.tsx';
import { useCreateWithPlannerItem } from '@jira/ai-first-create__app/src/useCreateWithPlannerItem.tsx';
import {
	AiPlannerIframeProvider,
	useAiPlannerIframe,
} from '@jira/ai-planning__ai-planner-iframe-modal/src/AiPlannerIframeProvider.tsx';
import { useAiPlannerUniversalCreateEnabled } from '@jira/ai-planning__ai-plans-overview/src/useAiPlannerUniversalCreateEnabled.tsx';
import { useAiFirstCreateEnabled } from '@jira/router-resources__ai-first-create/src/useAiFirstCreateEnabled.tsx';
import { NavigationCreateIssueButtonWrapperNudgeAsync } from '@jira/software__onboarding-nudges/src/NavigationCreateIssueButtonWrapperNudgeAsync.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { loadEntryPoint } from 'react-relay';
import { TopLevelErrorBoundary } from '@jira/navigation-apps__topnav-common/src/ui/error-boundary/index.tsx';
import { globalIssueCreateModernisedModalEntryPoint } from '@jira/issue-create__issue-create-modernised/src/modal/globalIssueCreateModernisedModal.entrypoint.tsx';
import { preloadIssueCreateModernisedModal } from '@jira/issue-create__issue-create-modernised/src/modal/AsyncIssueCreateModernisedModal.tsx';
import { preloadModalLoadQuery } from '@jira/issue-create__issue-create-modal/src/services/modal-load-query/preloadModalLoadQuery.tsx';
import { SUPPORTED_MODERNISED_PROJECT_TYPES } from '@jira/issue-create__issue-create-common-types/src/common/constants/index.tsx';
import { useStorageVariables } from '@jira/issue-create__issue-create-modal/src/ui/modal/utils.tsx';
import { useSmartFieldsMachineLearning } from '@jira/issue-create__issue-create-modal/src/common/utils/use-smart-fields-ml/index.tsx';
import {
	useTriggerIssueCreateModal,
	initialState,
	noopActions,
} from '@jira/issue-create__use-trigger-issue-create-modal/src/main.tsx';
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx'; // eslint-disable-line jira/restricted/@jira+platform__services-providers-project-context
import { Box } from '@atlaskit/primitives/compiled/box';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { useEntryPointPreload } from '@atlassian/entry-point-use-trigger/preload';
import Placeholder from '@atlassian/jira-placeholder';
import type { UniversalCreateMenuSection } from '@atlassian/universal-create/types';
import { UniversalCreateButton } from '@atlassian/universal-create/UniversalCreateButton';

import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';

import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useOrgId } from '@atlassian/jira-tenant-context-controller/src/components/org-id/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required -- package export path is required for this entrypoint import
import { LazyLoadedSmartCreationModal } from '@atlassian/jira-smart-creation/jira-smart-creation/entrypoint';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { SpotlightTarget } from '@atlassian/jira-spotlight/src/ui/SpotlightTarget.tsx';
import {
	GLOBAL_CREATE_BUTTON_NAV4,
	GLOBAL_CREATE_BUTTON_WRAPPER,
	UNIVERSAL_CREATE_BUTTON_ID,
} from './constants.tsx';
import { useCreateIssueItem } from './hooks/use-universal-create-items.tsx';
import { useUniversalCreateAnalytics } from './hooks/use-analytics-tracker.tsx';
import {
	useUniversalCreateLoadExperience,
	useUniversalCreateButtonInteractiveExperienceStart,
	useUniversalCreateButtonInteractiveExperienceComplete,
} from './hooks/use-universal-create-ufo.tsx';
import { useShouldShowDefaultCreateButton } from './hooks/useShouldShowDefaultCreateButton.tsx';

const create = defineMessage({
	defaultMessage: 'Create',
	id: 'atlassian-navigation.universal-create.create',
	description: 'Create text for the universal create button',
});

const noopPreload = (): void => {
	// No-op: hover preload is disabled when the on-hover gate is off.
};

const emptyGlobalIssueCreateModernisedModalEntryPointParams = {};
const gateOffIssueCreateState = [initialState, noopActions] as const;

const shouldPreloadModernisedGlobalCreate = (
	projectContext: ReturnType<typeof useProjectContext>['data'],
	shouldUseLegacyCreateExperience: boolean,
): boolean =>
	!shouldUseLegacyCreateExperience &&
	(!projectContext?.projectType ||
		SUPPORTED_MODERNISED_PROJECT_TYPES.includes(projectContext.projectType));

const useGicHoverApiPreload = functionWithCondition(
	() => fg('nike_united_gic_on_hover_') && !fg('relay-migration-gic-phase-1'),
	(): (() => void) => {
		const { data: projectContext } = useProjectContext();
		const storageVariables = useStorageVariables();
		const { withMachineLearning } = useSmartFieldsMachineLearning();
		return useCallback(() => {
			preloadModalLoadQuery({ projectContext, storageVariables, withMachineLearning });
		}, [projectContext, storageVariables, withMachineLearning]);
	},
	(): (() => void) => noopPreload,
);

const useUniversalCreateIssueCreateState = functionWithCondition(
	() => fg('relay-migration-gic-phase-1'),
	useTriggerIssueCreateModal,
	() => gateOffIssueCreateState,
);

const useGlobalIssueCreateModernisedModalEntryPointPreload = functionWithCondition(
	() => fg('relay-migration-gic-phase-1'),
	(): (() => void) => {
		const relayEnvironmentProvider = useRelayEnvironmentProvider();
		const loadGlobalIssueCreateModernisedModalEntryPoint = useCallback(
			() =>
				loadEntryPoint(
					relayEnvironmentProvider,
					globalIssueCreateModernisedModalEntryPoint,
					emptyGlobalIssueCreateModernisedModalEntryPointParams,
				),
			[relayEnvironmentProvider],
		);
		const { preloadReference } = useEntryPointPreload(
			loadGlobalIssueCreateModernisedModalEntryPoint,
		);

		return preloadReference;
	},
	(): (() => void) => noopPreload,
);

function UniversalCreateInner() {
	const [isAifcModalOpen, setIsAifcModalOpen] = useState(false);
	const { formatMessage } = useIntl();
	const triggerAnalytics = useUniversalCreateAnalytics();
	const aiPlannerEnabled = useAiPlannerUniversalCreateEnabled();
	const cloudId = useCloudId();
	const accountId = useAccountId() || '';
	const orgId = useOrgId() || '';
	// oxlint-disable-next-line react-hooks/rules-of-hooks, react/react-compiler
	const sharedPlannerIframe = aiPlannerEnabled ? useAiPlannerIframe() : undefined;
	const warmPlanner = sharedPlannerIframe?.warm;

	useUniversalCreateLoadExperience();
	useUniversalCreateButtonInteractiveExperienceStart();

	const createIssueItem = useCreateIssueItem({});
	const createWithAiItem = useCreateWithAiItem({ onItemClick: setIsAifcModalOpen });
	const createWithPlannerItem = aiPlannerEnabled
		? // oxlint-disable-next-line react-hooks/rules-of-hooks, react/react-compiler
			useCreateWithPlannerItem()
		: undefined;

	const aiFirstCreateEnabled = useAiFirstCreateEnabled() && !fg('jira_planner_uc_enabled');
	const plannerCreateEnabled = aiPlannerEnabled && createWithPlannerItem !== undefined;
	const shouldShowDefaultCreateButton = useShouldShowDefaultCreateButton();
	const { data: projectContext } = useProjectContext();
	const [{ shouldUseLegacyCreateExperience }] = useUniversalCreateIssueCreateState();
	const preloadGlobalIssueCreateModernisedModalEntryPoint =
		useGlobalIssueCreateModernisedModalEntryPointPreload();

	const menuSection = useMemo(() => {
		const hasCreateWithAiOptions = aiFirstCreateEnabled || plannerCreateEnabled;

		if (hasCreateWithAiOptions) {
			return {
				items: [
					...(aiFirstCreateEnabled ? [createWithAiItem] : []),
					...(plannerCreateEnabled ? [createWithPlannerItem] : []),
					...(!fg('jira_uc_split_button_enabled') ? [createIssueItem] : []),
				],
			};
		}
		return {
			items: [createIssueItem],
		};
	}, [
		aiFirstCreateEnabled,
		plannerCreateEnabled,
		createIssueItem,
		createWithAiItem,
		createWithPlannerItem,
	]);

	const sections = useMemo(
		(): UniversalCreateMenuSection[] => [
			{
				type: 'appDefinedSection',
				items: menuSection.items,
			},
		],
		[menuSection.items],
	);

	// Primary button needs its own onClick that fires analytics with isPrimary: true
	const primaryActionItem = useMemo(
		() => ({
			...createIssueItem,
			onClick: (event: React.MouseEvent | React.KeyboardEvent) => {
				triggerAnalytics({ isPrimary: true });
				createIssueItem.onClick?.(event);
			},
		}),
		[createIssueItem, triggerAnalytics],
	);

	// Mark interactive experience as complete (items are loaded)
	useUniversalCreateButtonInteractiveExperienceComplete();

	const onDropdownMenuOpen = useCallback(() => {
		// Preload the AI SmartCreationModal on page load
		LazyLoadedSmartCreationModal.preload();
		if (aiPlannerEnabled) {
			warmPlanner?.();
		}
		triggerAnalytics();
	}, [aiPlannerEnabled, triggerAnalytics, warmPlanner]);

	const onDefaultCreateClick = useCallback(() => {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		createIssueItem?.onClick?.({ timeStamp: performance.now() } as React.MouseEvent);
	}, [createIssueItem]);

	const preloadGicApiOnHover = useGicHoverApiPreload();

	const hasPreloadedModernisedModalRef = useRef(false);
	const hasPreloadedGicApiRef = useRef(false);
	const handlePreloadTrigger = useCallback(() => {
		if (
			!hasPreloadedGicApiRef.current &&
			!fg('relay-migration-gic-phase-1') &&
			fg('nike_united_gic_on_hover_')
		) {
			hasPreloadedGicApiRef.current = true;
			preloadGicApiOnHover();
		}

		if (
			!shouldPreloadModernisedGlobalCreate(
				projectContext,
				shouldUseLegacyCreateExperience ?? false,
			) &&
			fg('relay-migration-gic-phase-1')
		) {
			return;
		}

		if (hasPreloadedModernisedModalRef.current) {
			return;
		}

		if (!fg('jira_preload_modernised_create_modal_on_hover')) {
			return;
		}

		hasPreloadedModernisedModalRef.current = true;
		if (fg('relay-migration-gic-phase-1')) {
			preloadGlobalIssueCreateModernisedModalEntryPoint();
			return;
		}

		preloadIssueCreateModernisedModal();
	}, [
		preloadGicApiOnHover,
		preloadGlobalIssueCreateModernisedModalEntryPoint,
		projectContext,
		shouldUseLegacyCreateExperience,
	]);

	const conditionalProps = () => {
		if (shouldShowDefaultCreateButton) {
			return { buttonOnly: true as const, onClick: onDefaultCreateClick };
		}

		const menuSections = {
			sections,
		};

		// UC split button variant
		if (fg('jira_uc_split_button_enabled')) {
			return { primaryActionItem, onClick: onDropdownMenuOpen, ...menuSections };
		}

		// UC single button variant
		return { onClick: onDropdownMenuOpen, ...menuSections };
	};
	return (
		<Box
			testId={`${GLOBAL_CREATE_BUTTON_WRAPPER}`}
			onMouseEnter={handlePreloadTrigger}
			onFocus={handlePreloadTrigger}
		>
			{isAifcModalOpen && (
				<Placeholder name="JiraSmartCreationModal" fallback={null}>
					<LazyLoadedSmartCreationModal
						cloudId={cloudId}
						accountId={accountId}
						orgId={orgId}
						onClose={() => setIsAifcModalOpen(false)}
					/>
				</Placeholder>
			)}
			<NavigationCreateIssueButtonWrapperNudgeAsync>
				<CreateButtonSpotlightTarget>
					<UniversalCreateButton
						product="jira"
						testId={GLOBAL_CREATE_BUTTON_NAV4}
						{...conditionalProps()}
					>
						{formatMessage(create)}
					</UniversalCreateButton>
				</CreateButtonSpotlightTarget>
			</NavigationCreateIssueButtonWrapperNudgeAsync>
		</Box>
	);
}

const CreateButtonSpotlightTarget = ({ children }: PropsWithChildren) => {
	return <SpotlightTarget name="global-create-spotlight">{children}</SpotlightTarget>;
};

const UniversalCreateWithPlannerProviders = (): React.JSX.Element => {
	const cloudId = useCloudId();
	const accountId = useAccountId() || '';
	const embedContext = useMemo(() => ({ cloudId, accountId }), [cloudId, accountId]);

	return (
		<AiPlannerIframeProvider embedContext={embedContext}>
			<ModalContextProvider>
				<UniversalCreateInner />
			</ModalContextProvider>
		</AiPlannerIframeProvider>
	);
};

export default function UniversalCreate(): React.JSX.Element {
	return (
		<TopLevelErrorBoundary id={UNIVERSAL_CREATE_BUTTON_ID}>
			{fg('jira_planner_uc_enabled') ? (
				<UniversalCreateWithPlannerProviders />
			) : (
				<UniversalCreateInner />
			)}
		</TopLevelErrorBoundary>
	);
}
